import { datadogRum } from '@datadog/browser-rum';
import { APP_CONFIG, datadogConfig } from 'datadog/constants/app-config';
import { getSessionUrl } from '../fullstory/index';
import { filterSensitiveHeaders, parseXHRHeaders, decodePayload } from 'common/utilities/utils';

(function instrumentXHR() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__METHOD__ = method;
    this.__URL__ = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    this._requestPayload = body;
    return originalSend.call(this, body);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (header, value) {
    this._requestHeaders = this._requestHeaders || {};
    this._requestHeaders[header] = value;
    return originalSetRequestHeader.call(this, header, value);
  };
})();

let fullStoryUpdateInterval;

export const initDatadogRum = (
  appEnvironment,
  dataDogSessionReplaySampleRate,
  dataDogSessionSampleRate,
  dataDogApplicationId,
  dataDogClientId,
  dataDogSite,
  dataDogService,
  profileReducer,
) => {
  try {
    datadogRum.init({
      applicationId: dataDogApplicationId,
      clientToken: dataDogClientId,
      site: dataDogSite,
      service: dataDogService,
      env: `${appEnvironment}_${process.env.REACT_APP_TENANT_CODE}`.toLowerCase(),
      version: APP_CONFIG.VERSION,
      sessionSampleRate: dataDogSessionSampleRate,
      sessionReplaySampleRate: dataDogSessionReplaySampleRate,
      trackInteractions: true,
      trackFrustrations: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: datadogConfig.defaultPrivacyLevel,
      beforeSend: (event, context) => {
        if (event.type === 'error') {
          if (event.error?.message?.includes(datadogConfig.DATADOG_EXCLUDE_OPTIMIZELY_ERROR_MSG)) {
            return false;
          }
        }
        if (event.type === 'resource' && event.resource.type === 'xhr') {
          const xhr = context?.xhr;
          const sessionUrl = getSessionUrl();
          if (xhr) {
            event.context = event.context || {};

            event.context = {
              request_url: xhr.__URL__ || xhr.responseURL,
              request_method: xhr.__METHOD__ || xhr.requestMethod,
              request_headers: filterSensitiveHeaders(xhr._requestHeaders) || {},
              request_payload: decodePayload(xhr._requestPayload) || null,
              response_status: xhr.status,
              response_headers: parseXHRHeaders(xhr.getAllResponseHeaders()),
              fullStorySessionUrl: sessionUrl,
            };

            if (xhr.status >= 400) {
              try {
                event.context.response_body = xhr.responseText;
              } catch {
                event.context.response_body = 'Unable to read response body';
              }
            }
          }
        }

        return true;
      },
    });
    if (profileReducer) {
      datadogRum.setUser({
        id: profileReducer.basic?.id,
        email: profileReducer.basic?.email,
        name: `${profileReducer.basic?.firstName || ''} ${profileReducer.basic?.lastName || ''}`.trim(),
        customerId: profileReducer.customerId,
      });
    }

    datadogRum.startSessionReplayRecording();
    const updateFullStoryContext = () => {
      try {
        const sessionUrl = getSessionUrl();
        if (!sessionUrl) {
          return;
        }

        datadogRum.setGlobalContext({
          fullStorySessionUrl: sessionUrl ? sessionUrl : 'No session URL Available',
          market: `${process.env.REACT_APP_TENANT_CODE}`,
          Timestamp: new Date().toString(),
        });

        cleanupDatadogRum();
      } catch (error) {
        console.warn('Failed to update FullStory URL in DataDog:', error);
      }
    };

    updateFullStoryContext();

    fullStoryUpdateInterval = setInterval(updateFullStoryContext, 10000);
  } catch (error) {
    console.warn('Unable to initialise datadog for KFC-PWA, reason:', error);
  }
};

export const cleanupDatadogRum = () => {
  if (fullStoryUpdateInterval) {
    clearInterval(fullStoryUpdateInterval);
  }
};
